import moment from 'moment'

import { ModalCalendar } from '@primitive/modal.calendar'

import { IContracts } from './types'
import { ContractsFilter } from './ui.filter'
import { ContractReportsDrawer } from './ui.reports'

import CommonService from '@services/common.service'

import { CardContractContainer } from '@smart/cardContract'
import { CardListenerContainer } from '@smart/cardListener'
import { ComponentsHeader } from '@primitive/sectionHeadings'

import imgEmty from '@img/empty.svg'
import imgAccessDenied from '@img/access_denied.svg'
import logoYaSkill from '@img/logo_yaskill.svg'
import logoModSkill from '@img/logo_modskill.svg'
import { 
    IconContracts, IconDelivery, IconPrint, IconJobs, IconScanFile, IconMAX, IconTG
} from '@img/icons'

import { Button, Empty, Flex, Pagination, Loader, Table, ToolTip } from '@ui/index'

import css from './ui.module.scss'

/** Договора */
export const Contracts = ({
    isAccessDenied,
    isLoading, txtSearch, setTxtSearch, handleRefreshData, loadPrimaryData, handleOpenCardContract, handleOpenCardListener,
    modalCalendar, cardContract, cardListener, contractsList, contractsStatistics, totalCount, pageSize, currentPageNumber,
    
    filterIsOpen, filterIsActivate, filterSites, filterContractsStatuses, 
    filterDateStartEducationFrom, filterDateStartEducationBefore, 
    filterDateEndEducationFrom, filterDateEndEducationBefore,
    filterDateCreateFrom, filterDateCreateBefore, filterIsCompany,
    handleApplyClearFilter, handleOpenModalCalendar, contractReports
}: IContracts) => {
    const formatter = new Intl.NumberFormat('ru-RU')

    /** Вазвращает лого сайта */
    const returnLogo = (dbName: string) => {
        switch (dbName) {
            case 'modern_academy': return logoModSkill
            case 'yaskill': return logoYaSkill
        }
    }

    const getChatAction = (maxChatUrl: string | null, telegramChatUrl: string | null) => {
        const normalizedMaxChatUrl = CommonService.normalizeHttpsUrl(maxChatUrl)
        if (normalizedMaxChatUrl) return {href: normalizedMaxChatUrl, channel: 'MAX' as const}

        const normalizedTelegramChatUrl = CommonService.normalizeHttpsUrl(telegramChatUrl)
        if (normalizedTelegramChatUrl) return {href: normalizedTelegramChatUrl, channel: 'Telegram' as const}

        return null
    }

    const renderChatAction = (maxChatUrl: string | null, telegramChatUrl: string | null) => {
        const chatAction = getChatAction(maxChatUrl, telegramChatUrl)
        if (!chatAction) return null

        return <ToolTip title='Открыть чат' placement='bottomRight'>
            <Button variant='icon' size='xs' onClick={(event) => {
                event.stopPropagation()
                window.open(chatAction.href, '_blank', 'noopener,noreferrer')
            }}>
                <span aria-hidden>
                    {chatAction.channel === 'MAX'
                        ? <IconMAX width='16px' height='16px' color='#DC1C2E' />
                        : <IconTG width='16px' height='16px' color='#0088CC' />
                    }
                </span>
                <span className={css['visually_hidden']}>Открыть чат {chatAction.channel}</span>
            </Button>
        </ToolTip>
    }


    const footer = (
        <div className={css['footer']}>
            <Flex gap='4px' flexDirection="row">
                <ToolTip title='Общее количество' placement="topLeft">
                    <p className={css['stat_all']}>{totalCount}</p>
                </ToolTip>

                {contractsStatistics.new > 0 &&
                    <ToolTip title='Новые' placement="topLeft">
                        <p className={css['stat_new']}>{contractsStatistics.new}</p>
                    </ToolTip>
                }

                {contractsStatistics.signature > 0 &&
                    <ToolTip title='На подпись' placement="topLeft">
                        <p className={css['stat_signature']}>{contractsStatistics.signature}</p>
                    </ToolTip>
                }

                {contractsStatistics.waitingAccess > 0 &&
                    <ToolTip title='Ждут доступ' placement="topLeft">
                        <p className={css['stat_waitingAccess']}>{contractsStatistics.waitingAccess}</p>
                    </ToolTip>
                }

                {contractsStatistics.studying > 0 &&
                    <ToolTip title='Обучаются' placement="topLeft">
                        <p className={`${css['stat_studying']} ${css['hidden_590']}`}>{contractsStatistics.studying}</p>
                    </ToolTip>
                }

                {contractsStatistics.studyingEnd > 0 &&
                    <ToolTip title='Закончили' placement="topLeft">
                        <p className={`${css['stat_studyingEnd']} ${css['hidden_590']}`}>{contractsStatistics.studyingEnd}</p>
                    </ToolTip>
                }

                {contractsStatistics.canceled > 0 &&
                    <ToolTip title='Отказ' placement="topLeft">
                        <p className={`${css['stat_canceled']} ${css['hidden_590']}`}>{contractsStatistics.canceled}</p>
                    </ToolTip>
                }
            </Flex>

            <Pagination totalCount={totalCount} pageSize={pageSize} currentPage={currentPageNumber.value} onChange={(e) => currentPageNumber.setValue(e)} />
        </div>
    )

    return <div className={css['container']}>
        <ModalCalendar {...modalCalendar.value} />
        <CardListenerContainer {...cardListener.value} />
        <CardContractContainer {...cardContract.value} />
        <ContractReportsDrawer {...contractReports} />
        
        <ContractsFilter 
            isOpen={filterIsOpen.value} handlerButtonCancel={() => filterIsOpen.setValue(false)} 
            filterSites={filterSites}
            filterContractStatuses={filterContractsStatuses} handleApplyClearFilter={handleApplyClearFilter}
            filterDateStartEducationFrom={filterDateStartEducationFrom} filterDateStartEducationBefore={filterDateStartEducationBefore}
            filterDateEndEducationFrom={filterDateEndEducationFrom} filterDateEndEducationBefore={filterDateEndEducationBefore}
            filterDateCreateFrom={filterDateCreateFrom} filterDateCreateBefore={filterDateCreateBefore} filterIsCompany={filterIsCompany}
            handleOpenModalCalendar={handleOpenModalCalendar}
        />


        <ComponentsHeader
            isAccessDenied={isAccessDenied.value}
            headerIcon={<IconContracts width="20px" height="20px" />}
            headerTitle="Договоры"
            txtSearch={txtSearch}
            txtSearchTooltip={<>Поиск по:<br />✓ Номеру договора<br />✓ Номеру заявки<br />✓ ИНН организации<br />✓ Фамилии клиента<br />✓ Имени клиента<br />✓ Отчеству клиента<br />✓ Телефону клиента<br />✓ Email клиента<br />✓ Наименованию контрагента<br />✓ Адресу доставки<br />✓ Наименованию курса</>}
            txtSearchOnChange={(e) => setTxtSearch(e.target.value)}
            txtSearchClearOnClick={() => setTxtSearch('')}
            btnReportsOnClick={contractReports.handleButtonOpen}
            btnUpdateOnClick={handleRefreshData}
            btnCreateOnClick={() => handleOpenCardContract(true, "new")}
            btnFilterOnClick={() => filterIsOpen.setValue(true)}
            btnFilterClearOnClick={() => handleApplyClearFilter('clear')}
            filterIsActivate={filterIsActivate.value}
        />

        <Table.Container footer={footer} footerPlacement='page'>
            <Loader isOpen={isLoading} backdrop title="Загрузка..." />
            <Table.Header paddingLeft='70px' paddingRight='18px' classNameContainer={css['hidden_670']} classNameContent={css['table_grid_columns_header']}>
                <p style={{ textAlign: 'center' }}>Номер заявки</p>
                <p>Номер договора<br />Дата договора</p>
                <p>Тип / Вид договора<br />Контрагент</p>
                <p className={css['hidden_900']}>Статус договора<br />Общая сумма / Долг</p>
                <p className={css['hidden_1000']} style={{ textAlign: 'center' }}>Дата создания</p>
            </Table.Header>

            <Table.Content>
                {!contractsList.length
                    ? <Empty image={isAccessDenied.value ? imgAccessDenied : imgEmty} title={isAccessDenied.value ? 'Доступ запрещён' : 'Данные отсутствуют'} />
                    : contractsList.map((item) => (
                        <Table.Row key={item.contractID} padding='5px' background={item.contractStatusColor}
                            onClick={() => handleOpenCardContract(true, "edit", item.contractID)}
                        // paddingFooter='0 6px' childrenFooter={<>
                        //     <p>{item.siteName}</p>
                        //     <div>{item.isLearningOutsideMoodle === 1 && <span id='tooltipBtnOutsideMoodle' style={{cursor: 'help'}}><IconOutsideMoodle width='18px' height='18px' color='green' /></span>}</div>
                        //     <div>{item.isPostPayment === 1 && <span id='tooltipBtnPostPayment' style={{cursor: 'help'}}><IconPostPayment width='18px' height='18px' color='green' /></span>}</div>
                        // </>}
                        >
                            <Flex>
                                <div style={{ textWrap: 'nowrap', overflow: 'hidden' }} className={css['table_grid_columns_content']}>
                                    <div className={css['site_actions']}>
                                        <img className={css['site_logo']} src={returnLogo(item.dataBase)} alt="Лого сайта" />
                                        {renderChatAction(item.maxChatUrl, item.telegramChatUrl)}
                                    </div>

                                    <p style={{ textAlign: 'center' }}>
                                        {item.applicationID}
                                    </p>

                                    <div style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
                                        <p>{item.contractNumber}</p>
                                        <p style={{ opacity: '.8' }}>{item.contractDate}</p>
                                    </div>

                                    <div style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
                                        <p>{item.companyName && '🔥'} {item.contractTypeName} / {item.contractViewName}</p>
                                        <p style={{ opacity: '.8' }}>{item.companyName || '----'}</p>
                                    </div>

                                    <div className={css['hidden_900']} style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
                                        <p>{item.contractStatusName}</p>
                                        <b style={{ opacity: '.8' }}>{formatter.format(Number(item.contractSumm))} ₽ 👉 {formatter.format(Number(item.contractPayment))} ₽</b>
                                    </div>

                                    <div className={css['hidden_1000']} style={{ textAlign: 'center' }}>
                                        <p>{moment(item.dateCreate).format('DD.MM.YYYY')}</p>
                                        <p>{moment(item.dateCreate).format('HH:mm:ss')}</p>
                                    </div>
                                </div>


                                <Table.Content isScrollVertical={false}>
                                    {item.listenersList.length === 0
                                        ? <Empty image={imgEmty} size='60px' title='Слушатели отсутствуют' color='#fff' />
                                        : item.listenersList.map((listener, index) => (
                                            <Table.Row key={index} padding='2px 12px' className={css['table_grid_columns_content_listener']} onClick={(e) => { handleOpenCardListener(true, 'edit', listener.listenerID); e.stopPropagation() }}>
                                                <div style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
                                                    <p>{listener.lastName} {listener.firstName} {listener.secondName}</p>
                                                    <p style={{ opacity: '.8' }}>{listener.documentTypeName} / {listener.attestationTypeName} / {listener.phoneNumber}</p>
                                                </div>

                                                <div className={css['hidden_900']} style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
                                                    <p>{listener.courseName}</p>
                                                    <p style={{ opacity: '.8' }}>{CommonService.formatHours(Number(listener.volumeHours))} / {listener.dateEducationStart} / {listener.dateEducationEnd}</p>
                                                </div>

                                                <Flex gap="6px" flexDirection="row" alignItems="center" justifyContent="center">
                                                    <ToolTip title={listener.isFinalExamination === 1 ? 'ИТ пройдено' : 'ИТ не пройдена'} placement="bottomRight">
                                                        <span id='tooltipBtnJob' style={{ cursor: 'help' }}>
                                                            <IconJobs width='22px' height='22px' color={listener.isFinalExamination === 1 ? 'green' : 'red'} />
                                                        </span>
                                                    </ToolTip>

                                                    <ToolTip title={!listener.printStatusID ? 'Печать не выполнена' : listener.printStatusID === 1 ? 'Печать в процессе' : 'Печать выполнена'} placement="bottomRight">
                                                        <span id='tooltipBtnPrint' style={{ cursor: 'help' }}>
                                                            <IconPrint width='22px' height='22px' color={!listener.printStatusID ? 'red' : listener.printStatusID === 1 ? '#b2b200' : 'green'} />
                                                        </span>
                                                    </ToolTip>

                                                    <ToolTip title={listener.isDelivery === 1 ? 'Доставка выполнена' : 'Доставка не выполнена'} placement="bottomRight">
                                                        <span id='tooltipBtnDeliv' style={{ cursor: 'help' }}>
                                                            <IconDelivery width='22px' height='22px' color={listener.isDelivery === 1 ? 'green' : 'red'} />
                                                        </span>
                                                    </ToolTip>

                                                    <ToolTip title={listener.isScan === 1 ? 'Сканирование выполнено' : 'Сканирование не выполнено'} placement="bottomRight">
                                                        <span id='tooltipBtnScan' style={{ cursor: 'help' }}>
                                                            <IconScanFile width='22px' color={listener.isScan === 1 ? 'green' : 'red'} />
                                                        </span>
                                                    </ToolTip>
                                                </Flex>
                                            </Table.Row>
                                        ))
                                    }
                                </Table.Content>
                            </Flex>
                        </Table.Row>
                    ))
                }
            </Table.Content>
        </Table.Container>

        
    </div>
}
